import MultiPolygon from 'ol/geom/MultiPolygon';
import Polygon from 'ol/geom/Polygon';
import type Geometry from 'ol/geom/Geometry';
import polygonClipping, { type MultiPolygon as ClippingMultiPolygon, type Polygon as ClippingPolygon } from 'polygon-clipping';
import type {
  DifferenceFragmentOperation,
  ExcludeFragmentOperation,
  GeometryEditHistory,
  GeometryEditOperation,
  GeometryEditSession,
  LayerModel,
  LogicalFinca,
  LogicalSuerte,
  LogicalTablon,
  PhysicalFragment,
  Selection,
  SerializedPolygonGeometry,
  VectorLayerModel,
} from '../core/types';
import { measureArea } from './measurement';
import { validateFreeDifferencePolygon } from './boundaryCut';

const { difference, intersection } = polygonClipping;
const MAP_CRS = 'EPSG:3857';
const STORAGE_CRS = 'EPSG:4326';
/** Numerical guard only; it is not an agronomic/sliver-cleanup threshold. */
const ABSOLUTE_AREA_EPSILON_M2 = 0.0001;
const RELATIVE_AREA_EPSILON = 1e-12;
/** Tolerancia numérica de replay: corrige solo ruido de reproyección en un borde ya compartido por el recorte. */
const REPLAY_NUMERICAL_TOLERANCE_M = 1e-5;

export const emptyGeometryEditHistory = (): GeometryEditHistory => ({ commands: [], cursor: 0 });

export const appliedGeometryOperations = (history: GeometryEditHistory): readonly GeometryEditOperation[] =>
  history.commands.slice(0, history.cursor);

export const excludedFragmentIds = (history: GeometryEditHistory): ReadonlySet<string> =>
  new Set(appliedGeometryOperations(history).filter((operation): operation is ExcludeFragmentOperation => operation.kind === 'exclude-fragment').map(({ fragmentId }) => fragmentId));

const asClipping = (geometry: Geometry): ClippingPolygon | ClippingMultiPolygon => {
  if (geometry instanceof Polygon) return geometry.getCoordinates() as ClippingPolygon;
  if (geometry instanceof MultiPolygon) return geometry.getCoordinates() as ClippingMultiPolygon;
  throw new Error('La operación Difference requiere geometrías Polygon o MultiPolygon.');
};

const fromClipping = (coordinates: ClippingMultiPolygon): Polygon | MultiPolygon | null => {
  if (!coordinates.length) return null;
  return coordinates.length === 1 ? new Polygon(coordinates[0]!) : new MultiPolygon(coordinates);
};

type XY = [number, number];

const boundarySegments = (geometry: Polygon | MultiPolygon): readonly [XY, XY][] => {
  const polygons = geometry instanceof Polygon ? [geometry.getCoordinates()] : geometry.getCoordinates();
  return polygons.flatMap((polygon) => polygon.flatMap((ring) => ring.slice(0, -1).map((start, index) => [
    [start[0]!, start[1]!] as XY,
    [ring[index + 1]![0]!, ring[index + 1]![1]!] as XY,
  ] as [XY, XY])));
};

const projectNearBoundary = (coordinate: readonly number[], segments: readonly [XY, XY][]): XY => {
  for (const [start, end] of segments) {
    for (const vertex of [start, end]) {
      const vertexDistanceSquared = (coordinate[0]! - vertex[0]) ** 2 + (coordinate[1]! - vertex[1]) ** 2;
      if (vertexDistanceSquared <= REPLAY_NUMERICAL_TOLERANCE_M ** 2) return [...vertex];
    }
  }
  let closest: { coordinate: XY; distanceSquared: number } | null = null;
  for (const [start, end] of segments) {
    const dx = end[0] - start[0]; const dy = end[1] - start[1];
    const lengthSquared = dx * dx + dy * dy;
    const fraction = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1, ((coordinate[0]! - start[0]) * dx + (coordinate[1]! - start[1]) * dy) / lengthSquared));
    const projected: XY = [start[0] + fraction * dx, start[1] + fraction * dy];
    const distanceSquared = (coordinate[0]! - projected[0]) ** 2 + (coordinate[1]! - projected[1]) ** 2;
    if (!closest || distanceSquared < closest.distanceSquared) closest = { coordinate: projected, distanceSquared };
  }
  return closest && closest.distanceSquared <= REPLAY_NUMERICAL_TOLERANCE_M ** 2 ? closest.coordinate : [coordinate[0]!, coordinate[1]!];
};

const alignCutToActiveBoundary = (cut: Polygon | MultiPolygon, active: Polygon | MultiPolygon): Polygon | MultiPolygon => {
  const segments = boundarySegments(active);
  const alignPolygon = (coordinates: number[][][]): number[][][] => coordinates.map((ring) => ring.map((coordinate) => projectNearBoundary(coordinate, segments)));
  return cut instanceof Polygon
    ? new Polygon(alignPolygon(cut.getCoordinates()))
    : new MultiPolygon(cut.getCoordinates().map(alignPolygon));
};

const roundCoordinate = (value: number): number => Number(value.toFixed(12));

const serializeCutGeometry = (geometry: Polygon): SerializedPolygonGeometry => {
  const stored = geometry.clone().transform(MAP_CRS, STORAGE_CRS) as Polygon;
  return {
    type: 'Polygon',
    coordinates: stored.getCoordinates().map((ring) => ring.map((coordinate) => [roundCoordinate(coordinate[0]!), roundCoordinate(coordinate[1]!)] as const)),
  };
};

const finiteSerializedPolygon = (geometry: SerializedPolygonGeometry): boolean => geometry.type === 'Polygon'
  && geometry.coordinates.length > 0
  && geometry.coordinates.every((ring) => ring.length >= 4
    && ring.every((coordinate) => coordinate.length === 2 && coordinate.every(Number.isFinite))
    && ring[0]![0] === ring[ring.length - 1]![0]
    && ring[0]![1] === ring[ring.length - 1]![1]);

const deserializeCutGeometry = (geometry: SerializedPolygonGeometry): Polygon => {
  if (!finiteSerializedPolygon(geometry)) throw new Error('El historial SIG contiene una geometría de recorte inválida.');
  return new Polygon(geometry.coordinates.map((ring) => ring.map(([x, y]) => [x, y]))).transform(STORAGE_CRS, MAP_CRS) as Polygon;
};

interface DifferenceResult {
  readonly intersection: Polygon | MultiPolygon;
  readonly result: Polygon | MultiPolygon;
  readonly removedAreaHa: number;
  readonly resultAreaHa: number;
}

const calculateDifference = (active: Geometry, cut: Geometry): DifferenceResult => {
  if (!(active instanceof Polygon || active instanceof MultiPolygon) || !(cut instanceof Polygon || cut instanceof MultiPolygon)) {
    throw new Error('La operación Difference requiere geometrías Polygon o MultiPolygon.');
  }
  const alignedCut = alignCutToActiveBoundary(cut, active);
  let clipped: ClippingMultiPolygon;
  let remaining: ClippingMultiPolygon;
  try {
    clipped = intersection(asClipping(active), asClipping(alignedCut));
    remaining = difference(asClipping(active), asClipping(alignedCut));
  } catch {
    throw new Error('No fue posible calcular el recorte: revisa que el polígono dibujado sea válido.');
  }
  const clippedGeometry = fromClipping(clipped);
  if (!clippedGeometry) throw new Error('El polígono dibujado no intersecta el fragmento activo.');
  const activeAreaM2 = measureArea(active).areaM2;
  const removedAreaM2 = measureArea(clippedGeometry).areaM2;
  const epsilon = Math.max(ABSOLUTE_AREA_EPSILON_M2, activeAreaM2 * RELATIVE_AREA_EPSILON);
  if (!Number.isFinite(removedAreaM2) || removedAreaM2 <= epsilon) throw new Error('El polígono dibujado no produce una intersección medible con el fragmento activo.');
  const resultGeometry = fromClipping(remaining);
  if (!resultGeometry) throw new Error('El recorte eliminaría por completo el fragmento. Usa Excluir fragmento si deseas retirarlo.');
  const resultAreaM2 = measureArea(resultGeometry).areaM2;
  if (!Number.isFinite(resultAreaM2) || resultAreaM2 <= epsilon) throw new Error('El recorte dejaría una geometría nula o demasiado pequeña para conservarse.');
  return { intersection: clippedGeometry, result: resultGeometry, removedAreaHa: removedAreaM2 / 10_000, resultAreaHa: resultAreaM2 / 10_000 };
};

const fragmentWithGeometry = (source: PhysicalFragment, geometry: Polygon | MultiPolygon): PhysicalFragment => {
  const feature = source.feature.clone();
  feature.setGeometry(geometry);
  const featureId = source.feature.getId();
  if (featureId !== undefined) feature.setId(featureId);
  return { ...source, feature };
};

const replayFragments = (sourceModel: LayerModel, operations: readonly GeometryEditOperation[]): readonly PhysicalFragment[] => {
  const active = new Map(sourceModel.fragments.map((fragment) => [fragment.id, fragment]));
  operations.forEach((operation) => {
    const fragment = active.get(operation.fragmentId);
    if (!fragment) throw new Error(`El historial SIG referencia un fragmento inactivo: ${operation.fragmentId}.`);
    if (operation.kind === 'exclude-fragment') {
      active.delete(operation.fragmentId);
      return;
    }
    const geometry = fragment.feature.getGeometry();
    if (!geometry) throw new Error(`El fragmento ${operation.fragmentId} no contiene geometría.`);
    const result = calculateDifference(geometry, deserializeCutGeometry(operation.cutGeometry));
    active.set(operation.fragmentId, fragmentWithGeometry(fragment, result.result));
  });
  return sourceModel.fragments.flatMap((fragment) => {
    const candidate = active.get(fragment.id);
    return candidate ? [candidate] : [];
  });
};

const geometryAreaHa = (fragmentsById: ReadonlyMap<string, PhysicalFragment>, fragmentIds: readonly string[]): number => fragmentIds.reduce((sum, id) => {
  const geometry = fragmentsById.get(id)?.feature.getGeometry();
  return sum + (geometry ? measureArea(geometry).hectares : 0);
}, 0);

/** Replays the applied command prefix from immutable source records. */
export function deriveActiveLayerModel(sourceModel: LayerModel, history: GeometryEditHistory): LayerModel {
  const fragments = replayFragments(sourceModel, appliedGeometryOperations(history));
  const fragmentsById = new Map(fragments.map((fragment) => [fragment.id, fragment]));
  const activeIds = (ids: readonly string[]): string[] => ids.filter((id) => fragmentsById.has(id));
  const fincas = new Map<string, LogicalFinca>();
  sourceModel.fincas.forEach((source, id) => {
    const fragmentIds = activeIds(source.fragmentIds);
    fincas.set(id, { ...source, fragmentIds, areaHa: geometryAreaHa(fragmentsById, fragmentIds) });
  });
  const suertes = new Map<string, LogicalSuerte>();
  sourceModel.suertes.forEach((source, id) => {
    const fragmentIds = activeIds(source.fragmentIds);
    suertes.set(id, { ...source, fragmentIds, areaHa: geometryAreaHa(fragmentsById, fragmentIds) });
  });
  const tablones = new Map<string, LogicalTablon>();
  sourceModel.tablones.forEach((source, id) => {
    const fragmentIds = activeIds(source.fragmentIds);
    tablones.set(id, { ...source, fragmentIds, areaHa: geometryAreaHa(fragmentsById, fragmentIds) });
  });
  return { ...sourceModel, fragments, fragmentsById, fincas, suertes, tablones };
}

export function withActiveMasterModel(layers: readonly VectorLayerModel[], model: LayerModel): VectorLayerModel[] {
  return layers.map((layer) => layer.kind === 'master' && layer.id === model.id ? { ...layer, masterModel: model } : layer);
}

export function entityFragmentIds(model: LayerModel, entityKind: GeometryEditSession['entityKind'], entityId: string): readonly string[] {
  return entityKind === 'suerte' ? model.suertes.get(entityId)?.fragmentIds ?? [] : model.tablones.get(entityId)?.fragmentIds ?? [];
}

export interface GeometryEditTarget {
  readonly entityKind: GeometryEditSession['entityKind'];
  readonly entityId: string;
  readonly selectedFragmentId: string | null;
  readonly activeFragmentCount: number;
}

/** Resolves the logical editing parent without changing the current selection level. */
export function geometryEditTargetForSelection(model: LayerModel, selection: Selection): GeometryEditTarget | null {
  if (!selection || selection.kind === 'finca') return null;
  if (selection.kind === 'suerte' || selection.kind === 'tablon') {
    if (!entityFragmentIds(model, selection.kind, selection.id).length) return null;
    return { entityKind: selection.kind, entityId: selection.id, selectedFragmentId: null, activeFragmentCount: entityFragmentIds(model, selection.kind, selection.id).length };
  }
  const fragment = model.fragmentsById.get(selection.id);
  if (!fragment) return null;
  const tablonCount = entityFragmentIds(model, 'tablon', fragment.tablonId).length;
  const suerteCount = entityFragmentIds(model, 'suerte', fragment.suerteId).length;
  const parent = tablonCount > 1 || suerteCount <= 1
    ? { entityKind: 'tablon' as const, entityId: fragment.tablonId, activeFragmentCount: tablonCount }
    : { entityKind: 'suerte' as const, entityId: fragment.suerteId, activeFragmentCount: suerteCount };
  return { ...parent, selectedFragmentId: fragment.id };
}

/** Logical entities are consistently represented as MultiPolygon, even with one active physical record. */
export function logicalEntityGeometry(model: LayerModel, entityKind: GeometryEditSession['entityKind'], entityId: string): MultiPolygon | null {
  const coordinates = entityFragmentIds(model, entityKind, entityId).flatMap((id) => {
    const geometry = model.fragmentsById.get(id)?.feature.getGeometry();
    if (geometry instanceof Polygon) return [geometry.getCoordinates()];
    if (geometry instanceof MultiPolygon) return geometry.getCoordinates();
    return [];
  });
  return coordinates.length ? new MultiPolygon(coordinates) : null;
}

export interface GeometryEntityMetrics {
  readonly originalAreaHa: number;
  readonly excludedAreaHa: number;
  readonly activeAreaHa: number;
  readonly sourceFragmentCount: number;
  readonly activeFragmentCount: number;
}

export function geometryEntityMetrics(sourceModel: LayerModel, activeModel: LayerModel, entityKind: GeometryEditSession['entityKind'], entityId: string): GeometryEntityMetrics {
  const sourceIds = entityFragmentIds(sourceModel, entityKind, entityId);
  const activeIdsForEntity = entityFragmentIds(activeModel, entityKind, entityId);
  const sourceGeometry = logicalEntityGeometry(sourceModel, entityKind, entityId);
  const activeGeometry = logicalEntityGeometry(activeModel, entityKind, entityId);
  const originalAreaHa = sourceGeometry ? measureArea(sourceGeometry).hectares : 0;
  const activeAreaHa = activeGeometry ? measureArea(activeGeometry).hectares : 0;
  return { originalAreaHa, excludedAreaHa: Math.max(0, originalAreaHa - activeAreaHa), activeAreaHa, sourceFragmentCount: sourceIds.length, activeFragmentCount: activeIdsForEntity.length };
}

export const fragmentAreaHa = (model: LayerModel, fragmentId: string): number => {
  const geometry = model.fragmentsById.get(fragmentId)?.feature.getGeometry();
  return geometry ? measureArea(geometry).hectares : 0;
};

export function beginGeometryEdit(history: GeometryEditHistory, entityKind: GeometryEditSession['entityKind'], entityId: string): GeometryEditSession {
  return { entityKind, entityId, history: { commands: [...history.commands], cursor: history.cursor }, selectedFragmentId: null };
}

export function selectGeometryEditFragment(session: GeometryEditSession, fragmentId: string | null): GeometryEditSession {
  return { ...session, selectedFragmentId: fragmentId };
}

const appendOperation = (session: GeometryEditSession, command: GeometryEditOperation, selectedFragmentId: string | null): GeometryEditSession => {
  const commands = [...session.history.commands.slice(0, session.history.cursor), command];
  return { ...session, history: { commands, cursor: commands.length }, selectedFragmentId };
};

export function excludeFragment(session: GeometryEditSession, sourceModel: LayerModel, activeModel: LayerModel): GeometryEditSession {
  const fragmentId = session.selectedFragmentId;
  if (!fragmentId || !entityFragmentIds(sourceModel, session.entityKind, session.entityId).includes(fragmentId)) throw new Error('Selecciona un fragmento físico de la entidad en edición.');
  if (!activeModel.fragmentsById.has(fragmentId)) throw new Error('El fragmento ya está excluido de la geometría activa.');
  const remaining = entityFragmentIds(activeModel, session.entityKind, session.entityId);
  if (remaining.length <= 1) throw new Error('No se puede excluir el último fragmento activo de la entidad.');
  return appendOperation(session, { kind: 'exclude-fragment', fragmentId, entityKind: session.entityKind, entityId: session.entityId }, null);
}

export interface DifferenceFragmentPreview {
  readonly operation: DifferenceFragmentOperation;
  readonly fragmentId: string;
  readonly originalAreaHa: number;
  readonly activeAreaHa: number;
  readonly removedAreaHa: number;
  readonly resultAreaHa: number;
  readonly intersectionGeometry: Polygon | MultiPolygon;
  readonly resultGeometry: Polygon | MultiPolygon;
}

export function previewDifferenceFragment(session: GeometryEditSession, sourceModel: LayerModel, activeModel: LayerModel, drawn: Geometry): DifferenceFragmentPreview {
  const fragmentId = session.selectedFragmentId;
  if (!fragmentId || !entityFragmentIds(sourceModel, session.entityKind, session.entityId).includes(fragmentId)) throw new Error('Selecciona un fragmento físico de la entidad en edición.');
  const source = sourceModel.fragmentsById.get(fragmentId);
  const active = activeModel.fragmentsById.get(fragmentId);
  if (!source || !active) throw new Error('El fragmento seleccionado ya no está activo.');
  validateFreeDifferencePolygon(drawn);
  const activeGeometry = active.feature.getGeometry();
  if (!activeGeometry) throw new Error('El fragmento seleccionado no contiene geometría.');
  const operation: DifferenceFragmentOperation = {
    kind: 'difference-fragment', schemaVersion: 1, fragmentId,
    entityKind: session.entityKind, entityId: session.entityId,
    cutGeometry: serializeCutGeometry(drawn),
  };
  const result = calculateDifference(activeGeometry, deserializeCutGeometry(operation.cutGeometry));
  const sourceGeometry = source.feature.getGeometry();
  return {
    operation, fragmentId,
    originalAreaHa: sourceGeometry ? measureArea(sourceGeometry).hectares : 0,
    activeAreaHa: measureArea(activeGeometry).hectares,
    removedAreaHa: result.removedAreaHa,
    resultAreaHa: result.resultAreaHa,
    intersectionGeometry: result.intersection,
    resultGeometry: result.result,
  };
}

export function applyDifferenceFragment(session: GeometryEditSession, preview: DifferenceFragmentPreview): GeometryEditSession {
  if (preview.operation.entityKind !== session.entityKind || preview.operation.entityId !== session.entityId || preview.fragmentId !== session.selectedFragmentId) {
    throw new Error('La previsualización ya no corresponde al fragmento seleccionado.');
  }
  return appendOperation(session, preview.operation, preview.fragmentId);
}

export function undoGeometryEdit(session: GeometryEditSession): GeometryEditSession {
  return session.history.cursor <= 0 ? session : { ...session, history: { ...session.history, cursor: session.history.cursor - 1 }, selectedFragmentId: null };
}

export function redoGeometryEdit(session: GeometryEditSession): GeometryEditSession {
  return session.history.cursor >= session.history.commands.length ? session : { ...session, history: { ...session.history, cursor: session.history.cursor + 1 }, selectedFragmentId: null };
}

export const sameGeometryEditHistory = (left: GeometryEditHistory, right: GeometryEditHistory): boolean => JSON.stringify(left) === JSON.stringify(right);

const validOperationIdentity = (operation: Partial<GeometryEditOperation>, sourceModel: LayerModel): operation is GeometryEditOperation => {
  if (typeof operation.fragmentId !== 'string' || (operation.entityKind !== 'suerte' && operation.entityKind !== 'tablon') || typeof operation.entityId !== 'string') return false;
  const fragment = sourceModel.fragmentsById.get(operation.fragmentId);
  return Boolean(fragment && (operation.entityKind === 'suerte' ? fragment.suerteId : fragment.tablonId) === operation.entityId);
};

export function normalizeGeometryEditHistory(value: unknown, sourceModel: LayerModel | null): GeometryEditHistory {
  if (value === undefined || value === null) return emptyGeometryEditHistory();
  // A project without Maestro (only external layers) is saved with the empty history: nothing to validate against.
  const empty = typeof value === 'object' && Array.isArray((value as Partial<GeometryEditHistory>).commands) && (value as GeometryEditHistory).commands.length === 0 && (value as GeometryEditHistory).cursor === 0;
  if (!sourceModel && empty) return emptyGeometryEditHistory();
  if (!sourceModel || typeof value !== 'object') throw new Error('El historial SIG del proyecto no es válido.');
  const candidate = value as Partial<GeometryEditHistory>;
  if (!Array.isArray(candidate.commands) || !Number.isInteger(candidate.cursor) || candidate.cursor! < 0 || candidate.cursor! > candidate.commands.length) throw new Error('El historial SIG del proyecto está incompleto.');
  const commands = candidate.commands.map((raw) => {
    if (!raw || typeof raw !== 'object') throw new Error('El historial SIG contiene una operación inválida.');
    const operation = raw as Partial<GeometryEditOperation>;
    if ((operation.kind !== 'exclude-fragment' && operation.kind !== 'difference-fragment') || !validOperationIdentity(operation, sourceModel)) {
      throw new Error('El historial SIG contiene una operación desconocida o ajena a su entidad.');
    }
    if (operation.kind === 'difference-fragment') {
      const differenceOperation = operation as Partial<DifferenceFragmentOperation>;
      if (differenceOperation.schemaVersion !== 1 || !differenceOperation.cutGeometry || !finiteSerializedPolygon(differenceOperation.cutGeometry)) {
        throw new Error('El historial SIG contiene una geometría Difference inválida.');
      }
    }
    return operation as GeometryEditOperation;
  });
  const active = new Set(sourceModel.fragments.map(({ id }) => id));
  commands.forEach((operation, index) => {
    if (!active.has(operation.fragmentId)) throw new Error(`El historial SIG referencia un fragmento inactivo: ${operation.fragmentId}.`);
    if (operation.kind === 'exclude-fragment') {
      const entityIds = entityFragmentIds(sourceModel, operation.entityKind, operation.entityId);
      if (entityIds.filter((id) => active.has(id)).length <= 1) throw new Error('El historial SIG intentaría excluir el último fragmento activo de una entidad.');
      active.delete(operation.fragmentId);
    }
    replayFragments(sourceModel, commands.slice(0, index + 1));
  });
  return { commands, cursor: candidate.cursor! };
}
